import { Component, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DateConstants } from '@app/constants/date-constants';
import { QuoteRequestState } from '@app/shared';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { UserRole, UtilityHubIdentity, HasRole } from '@auth/models/utility-hub-identity';
import { Quote, QuoteState } from '@app/quoting/models/quote';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { Subscription } from 'rxjs';
import { YoureDone } from '@app/shared/models/youre-done';
import { isCustomJourneyEnabledSelector, isDynamicSelector, isMatrixJourneyEnabledSelector, isQuickQuoteJourneyEnabledSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { loggedInUtilityHubIdentitySelector, quoteSelector } from '@app/state/app.selectors';

@Component({
  selector: 'app-youre-done-content',
  templateUrl: './youre-done-content.component.html',
  styleUrls: ['./youre-done-content.component.scss'],
})
export class YoureDoneContentComponent implements OnInit, OnDestroy {
  public dateFormat = DateConstants.LOCALE_FORMAT_SHORT();

  public quoteRequestId: number;
  @Input() public quoteRequestState: QuoteRequestState;
  @Input() public youreDone: YoureDone;
  customJourneyEnabled: boolean;
  matrixJourneyEnabled: boolean;
  quickQuoteJourneyEnabled: boolean;
  powwrEnabled: boolean;
  public userIdentity: UtilityHubIdentity;
  public UserRole = UserRole;

  public quoteState: QuoteState;
  public quoteStateType = QuoteState;
  quote: Quote;
  selectQuote$: Subscription;
  route$: Subscription;
  uhIdentity$: Subscription;
  uhConfig$: Subscription;

  constructor(public quoteService: QuoteRequestService, private route: ActivatedRoute, private store: Store<AppState>) {}

  ngOnDestroy(): void {
    this.selectQuote$?.unsubscribe();
    this.route$?.unsubscribe();
    this.uhIdentity$?.unsubscribe();
    this.uhConfig$?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.quoteRequestState = changes.quoteRequestState.currentValue;
    this.selectQuote$ = this.store.select(quoteSelector).subscribe((s) => {
      this.quoteState = s?.quote?.quoteState;
      this.youreDone = s?.quote?.youreDone;
      this.quote = s?.quote;
    });
  }

  ngOnInit(): void {
    this.route$ = this.route.params.subscribe((x) => {
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
      }
    });

    this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((user) => {
      this.userIdentity = user;
    });

    this.store.select(isDynamicSelector).subscribe(d=> this.powwrEnabled = d);
    this.store.select(isCustomJourneyEnabledSelector).subscribe(c => this.customJourneyEnabled = c);
    this.store.select(isMatrixJourneyEnabledSelector).subscribe(m => this.matrixJourneyEnabled = m);
    this.store.select(isQuickQuoteJourneyEnabledSelector).subscribe(q => this.quickQuoteJourneyEnabled = q);
  }

  public isMatrixEnabled() {
    return this.matrixJourneyEnabled;
  }

  public isCustomEnabled() {
    return this.customJourneyEnabled;
  }

  public hasRole(userRole: UserRole) {
    return HasRole(this.userIdentity, userRole);
  }

  getTitle(): string {
    switch (this.quoteState) {
      case this.quoteStateType.OFFER_REJECTED: return 'Offer Rejected';
      case this.quoteStateType.OFFER_ACCEPTED: return 'Offer Accepted';
      case this.quoteStateType.ENROLLED: return 'Customer Enrolled';
      case this.quoteStateType.CONTRACT_REJECTED: return 'DocuSign Rejected';
      case this.quoteStateType.CONTRACT_PENDING: return 'DocuSign Submitted - Pending Signature from Customer';
      case this.quoteStateType.PENDING_ENROLLMENT: return 'Contract Submitted - Pending Enrollment';
      case this.quoteStateType.APPROVAL_ACCEPTED: return 'Offer Sent';
      case this.quoteStateType.APPROVAL_REJECTED: return 'Request Rejected';
      case this.quoteStateType.PARTIALLY_ENROLLED: return 'Contract Submitted - Partially Enrolled';
      case this.quoteStateType.CONTRACT_LIVE: return 'Contract Live';
      case this.quoteStateType.SUBMISSION_SUCCESSFUL: return 'Submission Successful';
      case this.quoteStateType.PARTIALLY_SUBMITTED: return 'Contract Submitted - Partially Submitted';
      case this.quoteStateType.CONTRACT_EXPIRING: return 'DocuSign Contract Expiring';
      case this.quoteStateType.CONTRACT_DROP_PENDING: return 'DocuSign Contract Drop Pending';
      case this.quoteStateType.CONTRACT_DROPPED: return 'DocuSign Contract Dropped';
      case this.quoteStateType.SUBMISSION_FAILED: return 'Contract Submitted - Submission Failed';
      default: return '';
    }
  }
  
  getOutcomeImage(): string {
    if (this.quoteState === this.quoteStateType.OFFER_REJECTED || this.quoteState === this.quoteStateType.CONTRACT_REJECTED ||
        this.quoteState === this.quoteStateType.APPROVAL_REJECTED || this.quoteState === this.quoteStateType.CONTRACT_EXPIRING ||
        this.quoteState === this.quoteStateType.CONTRACT_DROP_PENDING || this.quoteState === this.quoteStateType.CONTRACT_DROPPED ||
        this.quoteState === this.quoteStateType.SUBMISSION_FAILED) {
      return '/assets/images/rejected.png';
    }
    return '/assets/images/accepted.png';
  }
  
  hideDocuments(): boolean {
    return this.quoteState === this.quoteStateType.APPROVAL_ACCEPTED || this.quoteState === this.quoteStateType.APPROVAL_REJECTED ||
           this.quoteState === this.quoteStateType.PARTIALLY_ENROLLED || this.quoteState === this.quoteStateType.CONTRACT_LIVE ||
           this.quoteState === this.quoteStateType.SUBMISSION_SUCCESSFUL || this.quoteState === this.quoteStateType.PARTIALLY_SUBMITTED;
  }
  
  isQuoteOwner(): boolean {
    return this.quoteRequestState?.ownerUserId === this.userIdentity?.id;
  }
  
  getQuoteOwnerMessage(): string {
    switch (this.quoteState) {
      case this.quoteStateType.OFFER_REJECTED:
        return `An offer was rejected for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteRejectedDate).toLocaleDateString()}.`;
      case this.quoteStateType.OFFER_ACCEPTED:
        return `Great! An offer was accepted for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}.`;
      case this.quoteStateType.ENROLLED:
        return `Great News! The contract for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} was accepted and the customer was enrolled on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}.`;
      case this.quoteStateType.CONTRACT_REJECTED:
        return `Sorry! ${this.youreDone?.customerEmail || ''} rejected the DocuSign contract for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}.`;
      case this.quoteStateType.CONTRACT_PENDING:
        return `Great! An offer was accepted and a DocuSign contract submitted to ${this.youreDone?.customerEmail || ''} for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. Once the DocuSign has been signed and sent back, the contract will be submitted to the supplier.`;
      case this.quoteStateType.PENDING_ENROLLMENT:
        return `Great! An offer was accepted and a contract submitted to ${this.youreDone?.customerEmail || ''} for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. The contract has been submitted and is pending enrollment.`;
      case this.quoteStateType.APPROVAL_ACCEPTED:
        return `Great! You've sent an offer to ${this.youreDone?.ownerUserName || ''} for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}.`;
      case this.quoteStateType.APPROVAL_REJECTED:
        return `You rejected the request to quote for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteRejectedDate).toLocaleDateString()}.`;
      case this.quoteStateType.PARTIALLY_ENROLLED:
        return `Great! An offer was accepted and a contract submitted to ${this.youreDone?.customerEmail || ''} for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. The contract has been submitted and is partially enrolled.`;
      case this.quoteStateType.CONTRACT_LIVE:
        return `Great News! The contract for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} is now live.`;
      case this.quoteStateType.SUBMISSION_SUCCESSFUL:
        return `Great News! The contract for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} was accepted on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}.`;
      case this.quoteStateType.PARTIALLY_SUBMITTED:
        return `Great! An offer was accepted and a contract submitted to ${this.youreDone?.customerEmail || ''} for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. The contract has been partially submitted, and is awaiting full completion.`;
      case this.quoteStateType.CONTRACT_EXPIRING:
        return `Sorry! The DocuSign contract sent to ${this.youreDone?.customerEmail || ''} for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()} is about to expire.`;
      case this.quoteStateType.CONTRACT_DROP_PENDING:
        return `Sorry! The DocuSign contract sent to ${this.youreDone?.customerEmail || ''} for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()} was not complete, and will be dropped soon.`;
      case this.quoteStateType.CONTRACT_DROPPED:
        return `Sorry! The DocuSign contract sent to ${this.youreDone?.customerEmail || ''} for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()} was never completed, and has been dropped.`;
      case this.quoteStateType.SUBMISSION_FAILED:
        return `Sorry! Submission has failed for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}.`;
      default:
        return '';
    }
  }
  
  getNonQuoteOwnerMessage(): string {
    switch (this.quoteState) {
      case this.quoteStateType.OFFER_REJECTED:
        return `Sorry! ${this.quoteRequestState?.ownerUserFullName || ''} rejected the offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}.`;
      case this.quoteStateType.OFFER_ACCEPTED:
      case this.quoteStateType.ENROLLED:
        return `Great! ${this.quoteRequestState?.ownerUserFullName || ''} accepted your offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}.`;
      case this.quoteStateType.CONTRACT_REJECTED:
        return `Sorry! ${this.quoteRequestState?.ownerUserFullName || ''} rejected the DocuSign contract for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}.`;
      case this.quoteStateType.CONTRACT_PENDING:
      case this.quoteStateType.PENDING_ENROLLMENT:
        return `Great! ${this.quoteRequestState?.ownerUserFullName || ''} accepted your offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. The contract has been submitted and is pending enrollment.`;
      case this.quoteStateType.APPROVAL_ACCEPTED:
        return `Great! ${this.quoteRequestState?.ownerUserFullName || ''} accepted your offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}.`;
      case this.quoteStateType.APPROVAL_REJECTED:
        return `Sorry! ${this.quoteRequestState?.ownerUserFullName || ''} rejected the request to quote for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteRejectedDate).toLocaleDateString()}.`;
      case this.quoteStateType.PARTIALLY_ENROLLED:
        return `Great! ${this.quoteRequestState?.ownerUserFullName || ''} accepted your offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. The contract has been partially enrolled.`;
      case this.quoteStateType.CONTRACT_LIVE:
        return `Great News! The contract for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} is now live.`;
      case this.quoteStateType.SUBMISSION_SUCCESSFUL:
        return `Great! ${this.quoteRequestState?.ownerUserFullName || ''} accepted your offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. Submission successful.`;
      case this.quoteStateType.PARTIALLY_SUBMITTED:
        return `Great! ${this.quoteRequestState?.ownerUserFullName || ''} accepted your offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. The contract has been partially submitted and is awaiting full completion.`;
      case this.quoteStateType.CONTRACT_EXPIRING:
        return `Sorry! ${this.quoteRequestState?.ownerUserFullName || ''} accepted your offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. The DocuSign contract is about to expire.`;
      case this.quoteStateType.CONTRACT_DROP_PENDING:
        return `Sorry! ${this.quoteRequestState?.ownerUserFullName || ''} accepted your offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. The DocuSign contract was not completed and will be dropped soon.`;
      case this.quoteStateType.CONTRACT_DROPPED:
        return `Sorry! ${this.quoteRequestState?.ownerUserFullName || ''} accepted your offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. The DocuSign contract was not completed and has been dropped.`;
      case this.quoteStateType.SUBMISSION_FAILED:
        return `Sorry! ${this.quoteRequestState?.ownerUserFullName || ''} accepted your offer for ${this.quoteRequestState?.organisationName} - Quote ID ${this.quoteRequestState?.quoteRequestId} on ${new Date(this.youreDone?.quoteAcceptedDate).toLocaleDateString()}. Submission has failed.`;
      default:
        return '';
    }
  }
}
